import { createContext, useEffect, useMemo, useState } from 'react';
import { Store, User, Franchise } from '../../app/services/api/generated';
import { jwtDecode } from 'jwt-decode';
import { Role } from '../../enums/Role';

export interface UserType extends User {
    username?: string;
    roles?: string[];
    allAccessibleStores?: Store[];
}
interface UserContextType {
    user: UserType | null;
    token: string | null | undefined;

    selectedStore?: Store | null;
    setSelectedStore: (store: Store | null) => void;
    setUserStores: (stores: Store[]) => void;
    franchises: Franchise[];
    selectedFranchise?: Franchise | null;
    setSelectedFranchise: (franchise: Franchise | null) => void;
    setFranchises: (franchises: Franchise[]) => void;

    setUserContext: (user: User, token: string) => void;

    hasPermissionTo: (action: Role[]) => boolean;
    isAuthenticated: () => boolean;

    isAdmin: () => boolean;
}

const initialContextState: UserContextType = {
  user: null,
  token: null,

  selectedStore: null,
  selectedFranchise: null,
  setUserStores: (stores: Store[]) => { },

  setSelectedStore: (store: Store | null) => { },
  setSelectedFranchise: (franchise: Franchise | null) => { },
  setFranchises: (stores: Franchise[]) => { },
  setUserContext: () => { },
  hasPermissionTo: (action: Role[]) => false,
  isAuthenticated: () => false,
  isAdmin: () => false,
  franchises: [],
};

export const UserContext = createContext<UserContextType>(initialContextState);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const UserContextProvider = ({ children }: any) => {
  const [user, setUser] = useState<UserType | null>(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? (JSON.parse(storedUser)) : null;
  });
  const [selectedStore, setSelectedStore] = useState<Store | null>(() => {
    const storedStore = localStorage.getItem('selectedStore');
    return storedStore ? JSON.parse(storedStore) : null;
  });
  const [selectedFranchise, setSelectedFranchise] = useState<Franchise | null>(() => {
    const storedFranchise = localStorage.getItem('selectedFranchise');
    return storedFranchise ? JSON.parse(storedFranchise) : null;
  });

  const [franchises, setFranchises] = useState<Franchise[]>(() => {
    const storedFranchises = localStorage.getItem('franchises');
    return storedFranchises ? JSON.parse(storedFranchises) : [];
  });

  const [token, setToken] = useState<string | null>(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      try {
        const parsedToken = JSON.parse(storedToken);
        return typeof parsedToken === 'string' ? parsedToken : null;
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  useEffect(() => {
    localStorage.setItem('selectedStore', JSON.stringify(selectedStore));
    localStorage.setItem('selectedFranchise', JSON.stringify(selectedFranchise));
    localStorage.setItem('franchises', JSON.stringify(franchises));
    localStorage.setItem('token', JSON.stringify(token));

  }, [selectedStore, selectedFranchise, franchises, token]);

  // Retrieve user from local storage on component mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  const decodeJwtToken = (token: string) => {
    try {
      const decodedToken = jwtDecode(token);
      return decodedToken;
    } catch (err) {
      return null;
    }
  };

  const filterStoresByFranchise = (stores: Store[], franchise: Franchise | null) => {
    if (!franchise) return stores;
    return stores.filter(store => store.franchiseId === franchise.franchiseId);
  };

  const contextValue = useMemo(() => {
    const setUserContext = (userData: User, token: string) => {
      if (!userData?.persona?.personaRoles && token) {
        setToken(token);
        return;
      }

      const allAccessibleStores = userData?.stores ?? [];
      const normalizedRoles = userData?.persona?.personaRoles?.filter((role): role is string => !!role).map((role: string) => role.toLowerCase()) ?? [];
      // Only filter stores for non-admin users
      const isAdmin = normalizedRoles.includes('admin');
      const filteredStores = isAdmin ? allAccessibleStores : filterStoresByFranchise(allAccessibleStores, selectedFranchise ?? null);

      const user: UserType = {
        ...userData,
        username: userData?.emailAddress ?? '',
        roles: normalizedRoles,
        stores: filteredStores,
        allAccessibleStores: allAccessibleStores,
      };

      setUser(user);
      setToken(token);
      if (userData?.franchises) {
        setFranchises(userData.franchises);
      }
      localStorage.setItem('user', JSON.stringify(user));
    };

    const isAuthenticated = (): boolean => {
      if (token === undefined || token === null || token === '') return false;
      try {
        const decoded = jwtDecode(token);
        const { exp } = decoded;
        const currentTime = Math.floor(Date.now() / 1000);
        return !exp || currentTime < exp;
      }
      catch (err) {
        return false;
      }
    };

    const hasPermissionTo = (actions: Role[]): boolean => {
      if (!user?.roles) return false;

      if (user.roles.includes('admin')) return true;

      if (actions.some(a => a === 'User') && (user?.roles.length > 0)) {
        return true;
      }

      return actions.some(a => user.roles?.includes(a.toLowerCase()));
    };

    const isAdmin = () => {
      if (!user?.roles) return false; 
      return user.roles.includes('admin');
    };

    const setUserStores = (stores: Store[]) => {
      if (user == null) return;
      // Only filter stores for non-admin users
      const isAdmin = user?.roles?.includes('admin');
      const filteredStores = isAdmin ? stores : filterStoresByFranchise(stores, selectedFranchise ?? null);
      const updatedUser = { ...user, stores: filteredStores, allAccessibleStores: stores };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    };

    return {
      user,
      token,

      selectedStore,
      selectedFranchise,
      franchises,

      setUserContext,
      setToken,

      hasPermissionTo,
      isAuthenticated,
      isAdmin,

      setFranchises,
      setUserStores,
      setSelectedStore,
      setSelectedFranchise,
    };
  }, [user, selectedStore, selectedFranchise, franchises, token]);



  useEffect(() => {
    if (!user) return;
    const isAdmin = user.roles?.includes('admin');
    let updatedUser = user;

    // Update stores for non-admins
    if (!isAdmin) {
      const filteredStores = filterStoresByFranchise(user.allAccessibleStores ?? [], selectedFranchise ?? null);
      if (JSON.stringify(filteredStores) !== JSON.stringify(user.stores)) {
        updatedUser = { ...updatedUser, stores: filteredStores };
      }
    }

    // Update franchiseName if user has multiple franchises and selectedFranchise changes
    if (
      user.franchises &&
            user.franchises.length > 1 &&
            selectedFranchise &&
            user.franchiseName !== selectedFranchise.franchiseName
    ) {
      updatedUser = { ...updatedUser, franchiseName: selectedFranchise.franchiseName };
    }

    // Only update if something changed
    if (updatedUser !== user) {
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  }, [selectedFranchise]);

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};
